// Build the 80mm thermal-printer KOT slip HTML.
// `payload` is the response from GET /api/v1/kot/kots/:id/print
//   { kot: { kot_no, raised_at, table_no, guest_count, waiter_name, order_no,
//             property_name, outlet_name, outlet_code, station_name, ... },
//     items: [{ menu_item_name, kot_qty, order_qty, remarks }, ...] }

const ALLERGY_KEYWORDS = /allerg|peanut|nut|gluten|dairy|lactose|soy|shellfish|egg|wheat|sesame|sulfite/i;

const esc = (s) => String(s == null ? '' : s).replace(/[&<>"']/g, (c) => (
  { '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c]
));

function fmtDate(dt) {
  if (!dt) return '';
  const months = ['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec'];
  return `${String(dt.getDate()).padStart(2,'0')}-${months[dt.getMonth()]}-${dt.getFullYear()}`;
}

function fmtTime(dt) {
  if (!dt) return '';
  return `${String(dt.getHours()).padStart(2,'0')}:${String(dt.getMinutes()).padStart(2,'0')}`;
}

// Split a remarks string into individual modifier lines.
// Supports newline, semicolon, or comma separators.
function splitRemarks(raw) {
  if (!raw) return [];
  const s = String(raw).trim();
  if (!s) return [];
  const sep = s.includes('\n') ? '\n' : s.includes(';') ? ';' : ',';
  return s.split(sep).map(r => r.trim()).filter(Boolean);
}

function buildModifierRows(modifiers) {
  return modifiers.map(mod => {
    const isAllergy = ALLERGY_KEYWORDS.test(mod);
    const label = isAllergy
      ? `<span class="mod allergy">&gt; ${esc(mod.toUpperCase())} <span class="alg">(**)</span></span>`
      : `<span class="mod">&gt; ${esc(mod)}</span>`;
    return `<div class="modrow">${label}</div>`;
  }).join('');
}

export function buildKotPrintHtml(payload, fallbackPropertyName = '') {
  const k = payload?.kot || {};
  const items = Array.isArray(payload?.items) ? payload.items : [];

  const propertyName  = esc(k.property_name || fallbackPropertyName || '');
  const outletName    = esc(k.outlet_name || '');
  const stationName   = esc(k.station_name || '');
  const dt            = k.raised_at ? new Date(k.raised_at) : null;
  const dateStr       = fmtDate(dt);
  const timeStr       = fmtTime(dt);
  const tableNo       = esc(k.table_no || '-');
  const guests        = Number(k.guest_count || 0) || '-';
  const waiter        = esc(k.waiter_name || '');
  const orderNo       = k.order_no ? `#${k.order_no}` : '';
  const kotLabel      = k.outlet_code ? `${esc(k.outlet_code)}-${k.kot_no || ''}` : `#${k.kot_no || ''}`;

  const itemRows = items.map(it => {
    const name      = esc(it.menu_item_name || '-');
    const qty       = Number(it.kot_qty || it.order_qty || 0);
    const modifiers = splitRemarks(it.remarks);
    const modHtml   = buildModifierRows(modifiers);
    return `
      <tr>
        <td class="qty">${qty}</td>
        <td class="item">
          <span class="iname">${name}</span>
          ${modHtml}
        </td>
      </tr>`;
  }).join('');

  // Check if any item has an allergy modifier — show footer warning
  const hasAllergy = items.some(it => ALLERGY_KEYWORDS.test(it.remarks || ''));

  return `
    <html>
    <head>
      <title>KOT ${kotLabel}</title>
      <style>
        @page { size: 80mm auto; margin: 3mm 4mm; }
        html, body { margin: 0; padding: 0; }
        body {
          font-family: 'Courier New', Courier, monospace;
          color: #000;
          width: 80mm;
          padding: 4px 2px;
          box-sizing: border-box;
          font-size: 12px;
        }
        .sep  { border: none; border-top: 1px dashed #000; margin: 5px 0; }
        .sep2 { border: none; border-top: 2px solid #000; margin: 5px 0; }
        .hdr  { text-align: center; font-weight: 700; font-size: 14px; letter-spacing: 1px; padding: 2px 0; }
        .sub  { text-align: center; font-size: 11px; padding: 0 0 3px; }
        .row2 { display: flex; justify-content: space-between; font-size: 12px; padding: 2px 0; }
        .row2 .lbl { font-weight: 600; }
        table { width: 100%; border-collapse: collapse; }
        thead th { text-align: left; font-size: 11px; font-weight: 700; padding: 4px 0 2px; }
        thead th.qh { width: 28px; text-align: right; padding-right: 8px; }
        tbody tr { vertical-align: top; }
        td.qty  { width: 28px; text-align: right; padding: 4px 8px 4px 0; font-weight: 700; font-size: 13px; }
        td.item { padding: 4px 0; }
        .iname  { font-size: 12px; font-weight: 600; display: block; }
        .modrow { padding-left: 4px; }
        .mod    { font-size: 11px; color: #333; display: block; line-height: 1.4; }
        .allergy { font-weight: 700; color: #000; }
        .alg    { font-size: 10px; }
        .kotno  { display: flex; justify-content: space-between; font-size: 12px; font-weight: 700; padding: 4px 0 2px; }
        .algwarn { font-size: 10px; font-weight: 700; text-align: center; padding: 3px 0; border: 1px solid #000; margin: 4px 0; }
      </style>
    </head>
    <body>

      ${propertyName ? `<div class="hdr">${propertyName}</div>` : ''}
      ${outletName   ? `<div class="sub">${outletName}</div>` : ''}
      ${stationName  ? `<div class="sub">${stationName}</div>` : ''}

      <hr class="sep2"/>
      <div class="hdr">KOT — FIRED</div>
      <div class="hdr" style="font-size:12px;letter-spacing:2px">NEW ORDER</div>
      <hr class="sep2"/>

      <div class="row2"><span><span class="lbl">Date:</span> ${dateStr}</span><span><span class="lbl">Time:</span> ${timeStr}</span></div>
      <div class="row2"><span><span class="lbl">Table:</span> ${tableNo}</span><span><span class="lbl">Guests:</span> ${guests}</span></div>
      ${waiter  ? `<div class="row2"><span><span class="lbl">Server:</span> ${waiter}</span><span><span class="lbl">Order:</span> ${orderNo}</span></div>` : `<div class="row2"><span></span><span><span class="lbl">Order:</span> ${orderNo}</span></div>`}

      <hr class="sep"/>
      <table>
        <thead><tr><th class="qh">QTY</th><th>ITEM</th></tr></thead>
        <tbody>
          ${itemRows || '<tr><td colspan="2" style="padding:8px 0;text-align:center">No items</td></tr>'}
        </tbody>
      </table>
      <hr class="sep2"/>

      ${hasAllergy ? `<div class="algwarn">⚠ ALLERGY ITEMS — VERIFY BEFORE SERVING</div>` : ''}

      <div class="kotno"><span>KOT No</span><span>${kotLabel}</span></div>

    </body>
    </html>
  `;
}

// Convenience: fetch the KOT print payload and open a print popup.
export async function printKotById(kotId, headers, fallbackPropertyName = '') {
  const res = await fetch(`/api/v1/kot/kots/${kotId}/print`, { headers });
  const data = await res.json();
  if (!res.ok) throw new Error(data?.error || 'Failed to load KOT print');
  const w = window.open('', '_blank', 'width=700,height=760');
  if (!w) throw new Error('Enable popups to print KOT');
  w.document.write(buildKotPrintHtml(data, fallbackPropertyName));
  w.document.close();
  w.focus();
  w.print();
  return data;
}

// Build the 80mm bill/receipt HTML for a settled or open KOT/POS bill.
// `payload` is the response from GET /api/v1/kot/bills/:id
//   { bill: { bill_no, business_date, created_at, status, table_no, pax,
//              steward_name, order_no, kot_no, outlet_name, outlet_code,
//              billing_name, billing_address, gst_number, sub_total,
//              discount_total, gst_percent, cgst_total, sgst_total,
//              igst_total, tax_total, grand_total, settlement_mode,
//              refund_amount, refund_mode, refund_reason, refunded_at },
//     items: [{ menu_item_name, qty, unit_price, discount_amount,
//                cgst_amount, sgst_amount, igst_amount, tax_amount, line_total }],
//     payments: [{ payment_mode, amount, reference_no, paid_at }] }
function fmtMoney(n) {
  const v = Number(n || 0);
  return v.toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
}

// SAC for restaurant / food-service supply (GST).
const RESTAURANT_SAC = '996331';

function fmtDmy(dt) {
  if (!dt || Number.isNaN(dt.getTime())) return '';
  return `${String(dt.getDate()).padStart(2, '0')}/${String(dt.getMonth() + 1).padStart(2, '0')}/${dt.getFullYear()}`;
}

function fmtPct(p) {
  const v = Number(p || 0);
  return v.toFixed(1);
}

export function buildBillPrintHtml(payload) {
  const b = payload?.bill || {};
  const h = payload?.hotel || {};
  const items = Array.isArray(payload?.items) ? payload.items : [];
  const payments = Array.isArray(payload?.payments) ? payload.payments : [];

  // Header — outlet on top, then the hotel's billing identity (SELF billing
  // profile from PMS, falling back to the outlet's billing fields).
  const outletName  = esc(b.outlet_name || '');
  const hotelName   = esc(h.billing_company_name || b.billing_name || b.property_name || '');
  const hotelAddr   = esc(
    b.billing_address
    || [h.address_line1, h.address_line2, h.city, [h.state, h.pin_code].filter(Boolean).join(' - ')].filter(Boolean).join(', ')
  );
  const phone       = esc(h.phone || '');
  const email       = esc(h.email || '');
  const website     = esc(h.website || '');
  const gstNumber   = esc(b.gst_number || h.gstin_number || '');
  // Logo: outlet → billing profile (Front Office → Masters → Billing Info, the
  // PMS invoice logo) → property. The bill opens in an about:blank window /
  // srcDoc iframe, so a relative path is made absolute.
  const rawLogo     = String(b.outlet_logo_url || h.logo_url || b.property_logo_url || '').trim();
  const logoUrl     = rawLogo
    ? esc(/^(https?:|data:)/i.test(rawLogo)
      ? rawLogo
      : `${typeof window !== 'undefined' ? window.location.origin : ''}${rawLogo.startsWith('/') ? '' : '/'}${rawLogo}`)
    : '';

  const dt          = b.created_at ? new Date(b.created_at) : (b.business_date ? new Date(b.business_date) : null);
  const dateOnly    = dt ? fmtDmy(dt) : '';
  const timeOnly    = dt ? fmtTime(dt) : '';
  // A UUID here means the table number couldn't be resolved — never print the raw id.
  const rawTable    = String(b.table_no || '').trim();
  const tableNo     = esc(/^[0-9a-f]{8}-[0-9a-f]{4}-/i.test(rawTable) || !rawTable ? '-' : rawTable);
  const kotNos      = String(b.kot_nos || b.kot_no || '').trim().split(/\s*,\s*/).filter(Boolean).map((k) => `#${esc(k)}`).join(', ');
  const covers      = Number(b.pax || 0) || '-';
  const session     = esc(b.session_name || '-');
  const captain     = esc(b.steward_name || '-');
  const billLabel   = b.outlet_code ? `${esc(b.outlet_code)}-${b.bill_no || ''}` : `${b.bill_no || ''}`;

  const guestName   = esc(b.guest_name || b.customer_name_bill || '');
  const roomNo      = esc(b.room_number || '');
  const tableRoom   = roomNo ? `${tableNo} / ${roomNo}` : tableNo;
  const compName    = esc(b.customer_name_bill || '');
  const guestGst    = esc(b.customer_gstin || '');
  const isNc        = !!b.nc_reason;

  const hasIgst = items.some((it) => Number(it.igst_amount || 0) > 0) || Number(b.igst_total || 0) > 0;

  const itemRows = items.map((it) => {
    const name = esc(it.menu_item_name || '-');
    const qty = Number(it.qty || 0);
    const rate = Number(it.unit_price || 0);
    return `
      <tr>
        <td class="item">${name}</td>
        <td class="ctr">${qty}</td>
        <td class="num">${fmtMoney(rate)}</td>
        <td class="num">${fmtMoney(qty * rate)}</td>
      </tr>`;
  }).join('');

  const subTotal  = Number(b.sub_total || 0);
  const discount  = Number(b.discount_total || 0);
  const taxTotal  = hasIgst ? Number(b.igst_total || 0) : Number(b.cgst_total || 0) + Number(b.sgst_total || 0);
  const grand     = Number(b.grand_total || 0);
  const roundOff  = Math.round((grand - (subTotal - discount + taxTotal)) * 100) / 100;

  // GST rate printed beside each tax line. The bill header's stored percent is
  // a blended figure (tax ÷ pre-discount subtotal — e.g. 8.6% for a 9% rate
  // with a discount), so take the rate from the items: one common item rate
  // wins; mixed rates fall back to tax ÷ taxable value (after discount).
  const taxable = subTotal - discount;
  const ratePct = (itemKey, amount, headerPct) => {
    const rates = [...new Set(items
      .filter((it) => Number(it[itemKey] || 0) > 0)
      .map((it) => Math.round(Number(it[itemKey]) * 100) / 100))];
    if (rates.length === 1) return rates[0];
    if (taxable > 0 && Number(amount || 0) > 0) return Math.round((Number(amount) / taxable) * 10000) / 100;
    return Number(headerPct || 0);
  };
  const sgstPct = ratePct('sgst_percent', b.sgst_total, b.sgst_percent || (Number(b.gst_percent || 0) / 2));
  const cgstPct = ratePct('cgst_percent', b.cgst_total, b.cgst_percent || (Number(b.gst_percent || 0) / 2));
  const igstPct = ratePct('igst_percent', b.igst_total, b.igst_percent || b.gst_percent);

  const paymentRows = payments.map((p) => `
    <div class="row2"><span>${esc(p.payment_mode || '-')}${p.reference_no ? ` (${esc(p.reference_no)})` : ''}</span><span>${fmtMoney(p.amount)}</span></div>
  `).join('');

  const hasRefund = Number(b.refund_amount || 0) > 0;
  const now = new Date();
  const printedBy = esc(b.printed_by || '');

  return `
    <html>
    <head>
      <title>Bill ${billLabel}</title>
      <style>
        /* 80mm roll, 4mm side margins → 72mm printable width. */
        @page { size: 80mm auto; margin: 3mm 4mm; }
        * { box-sizing: border-box; }
        html, body { margin: 0; padding: 0; background: #fff; }
        body {
          font-family: Arial, Helvetica, sans-serif;
          color: #000;
          font-size: 12px;
          line-height: 1.3;
          font-variant-numeric: tabular-nums;
        }
        .bill { width: 72mm; margin: 0 auto; padding: 6px 0; }
        @media screen { body { padding: 8px 0; } }
        .sep  { border: none; border-top: 1px dashed #000; margin: 5px 0; }
        .sep2 { border: none; border-top: 1px solid #000; margin: 5px 0; }
        .logo { text-align: center; padding: 0 0 4px; }
        .logo img { max-width: 50mm; max-height: 20mm; object-fit: contain; }
        .outlet { text-align: center; font-weight: 700; font-size: 15px; letter-spacing: 1px; padding: 0 0 3px; }
        .hname  { text-align: center; font-weight: 700; font-size: 12.5px; padding: 1px 0; }
        .sub    { text-align: center; font-size: 11px; line-height: 1.35; word-break: break-word; }
        .title  { text-align: center; font-weight: 700; font-size: 13px; padding: 1px 0; }
        /* Two label/value columns: label · colon · value, colons line up. */
        .grid { display: grid; grid-template-columns: 1fr 1fr; column-gap: 8px; row-gap: 2px; font-size: 11.5px; }
        .kv { display: grid; grid-template-columns: 50px 8px minmax(0, 1fr); align-items: baseline; }
        .kv .k { font-weight: 600; white-space: nowrap; }
        .kv .v { white-space: nowrap; overflow: hidden; text-overflow: ellipsis; }
        .row2 { display: flex; justify-content: space-between; align-items: baseline; gap: 8px; font-size: 12px; padding: 1px 0; }
        .row2 > span:last-child { text-align: right; white-space: nowrap; }
        .row2.net { font-size: 15px; font-weight: 700; padding: 3px 0; }
        table { width: 100%; border-collapse: collapse; table-layout: fixed; }
        col.c-qty { width: 9mm; }
        col.c-rate { width: 15mm; }
        col.c-amt { width: 17mm; }
        thead th { text-align: left; font-size: 11.5px; font-weight: 700; padding: 2px 0 3px; border-bottom: 1px solid #000; }
        thead th.num, td.num { text-align: right; }
        thead th.ctr, td.ctr { text-align: center; }
        tbody td { padding: 3px 0; font-size: 11.5px; vertical-align: top; }
        tbody td.item { padding-right: 4px; word-break: break-word; }
        .line { display: grid; grid-template-columns: 26mm minmax(0, 1fr); align-items: end; font-size: 12px; padding: 3px 0; }
        .line .v { border-bottom: 1px dotted #000; min-height: 15px; padding-left: 2px; }
        .ghdr { text-align: center; font-weight: 700; text-decoration: underline; font-size: 12px; padding: 5px 0 1px; }
        .nc { text-align: center; font-weight: 700; border: 1px solid #000; padding: 3px 4px; margin: 4px 0; }
        .refund { font-weight: 700; padding: 3px 4px; border: 1px solid #000; text-align: center; margin: 4px 0; }
        .foot { display: flex; justify-content: space-between; gap: 8px; font-size: 11.5px; padding: 1px 0; }
        .thanks { text-align: center; font-weight: 700; font-size: 12px; padding: 4px 0 0; }
      </style>
    </head>
    <body>
    <div class="bill">

      ${logoUrl ? `<div class="logo"><img src="${logoUrl}" alt="" onerror="this.parentNode.style.display='none'"/></div>` : ''}
      ${outletName ? `<div class="outlet">${outletName.toUpperCase()}</div>` : ''}
      ${hotelName ? `<div class="hname">${hotelName.toUpperCase()}</div>` : ''}
      ${hotelAddr ? `<div class="sub">${hotelAddr.toUpperCase()}</div>` : ''}
      ${phone ? `<div class="sub">Phone ${phone}</div>` : ''}
      ${email ? `<div class="sub">Email: ${email}</div>` : ''}
      ${website ? `<div class="sub">Web site: ${website}</div>` : ''}
      ${gstNumber ? `<div class="sub">GST No:- ${gstNumber}</div>` : ''}

      <hr class="sep2"/>
      <div class="title">${b.status === 'SETTLED' ? 'TAX INVOICE' : 'BILL (UNSETTLED)'}</div>
      ${outletName ? `<div class="title" style="font-size:12px">${outletName.toUpperCase()}</div>` : ''}
      <hr class="sep2"/>

      <div class="grid">
        <div class="kv"><span class="k">Bill No</span><span>:</span><span class="v">${billLabel}</span></div>
        <div class="kv"><span class="k">Table No</span><span>:</span><span class="v">${tableNo}</span></div>
        <div class="kv"><span class="k">Date</span><span>:</span><span class="v">${dateOnly}</span></div>
        <div class="kv"><span class="k">Time</span><span>:</span><span class="v">${timeOnly}</span></div>
        <div class="kv"><span class="k">Session</span><span>:</span><span class="v">${session}</span></div>
        <div class="kv"><span class="k">Covers</span><span>:</span><span class="v">${covers}</span></div>
        <div class="kv"><span class="k">Captain</span><span>:</span><span class="v">${captain}</span></div>
        <div class="kv"><span class="k">SAC</span><span>:</span><span class="v">${RESTAURANT_SAC}</span></div>
      </div>

      <hr class="sep2"/>
      <table>
        <colgroup><col/><col class="c-qty"/><col class="c-rate"/><col class="c-amt"/></colgroup>
        <thead><tr><th>Item Name</th><th class="ctr">Qty</th><th class="num">Rate</th><th class="num">Amount</th></tr></thead>
        <tbody>
          ${itemRows || '<tr><td colspan="4" style="padding:8px 0;text-align:center">No items</td></tr>'}
        </tbody>
      </table>
      <hr class="sep2"/>

      <div class="row2"><span>Total Amount</span><span>₹ ${fmtMoney(subTotal)}</span></div>
      ${discount > 0 ? `<div class="row2"><span>Discount</span><span>-${fmtMoney(discount)}</span></div>` : ''}
      ${hasIgst
        ? `<div class="row2"><span>IGST @ ${fmtPct(igstPct)}%</span><span>${fmtMoney(b.igst_total)}</span></div>`
        : `<div class="row2"><span>State GST @ ${fmtPct(sgstPct)}%</span><span>${fmtMoney(b.sgst_total)}</span></div>
           <div class="row2"><span>Central GST @ ${fmtPct(cgstPct)}%</span><span>${fmtMoney(b.cgst_total)}</span></div>`}
      ${Math.abs(roundOff) >= 0.01 ? `<div class="row2"><span>Round Off</span><span>${roundOff > 0 ? '' : '-'}${fmtMoney(Math.abs(roundOff))}</span></div>` : ''}
      <hr class="sep2"/>
      <div class="row2 net"><span>Net Amount</span><span>₹ ${fmtMoney(grand)}</span></div>
      <hr class="sep2"/>

      ${kotNos ? `<div class="row2"><span><b>Kot No</b> ${kotNos}</span></div>` : ''}
      ${isNc ? `<div class="nc">COMPLIMENTARY — ${esc(b.nc_reason)}${b.nc_authorised_by ? ` (Auth: ${esc(b.nc_authorised_by)})` : ''}</div>` : ''}

      <div class="line"><span>Guest Name</span><span class="v">${guestName}</span></div>
      <div class="line"><span>Table/Room#</span><span class="v">${tableRoom}</span></div>
      <div class="line"><span>Guest Signature</span><span class="v"></span></div>

      <div class="ghdr">Guest GST Details</div>
      <div class="line"><span>GST Bill No#</span><span class="v">${billLabel}</span></div>
      <div class="line"><span>Comp/Guest</span><span class="v">${compName}</span></div>
      <div class="line"><span>GST No</span><span class="v">${guestGst}</span></div>

      ${payments.length ? `<hr class="sep"/><div class="title" style="font-size:12px">PAYMENTS</div>${paymentRows}` : ''}

      ${hasRefund ? `<hr class="sep"/><div class="refund">REFUNDED ₹${fmtMoney(b.refund_amount)} (${esc(b.refund_mode || '')})${b.refund_reason ? ` — ${esc(b.refund_reason)}` : ''}</div>` : ''}

      <hr class="sep2"/>
      <div class="foot"><span>Cashier : ${printedBy ? `${printedBy.toUpperCase()} (POS)` : ''}</span><span><b>${b.status === 'SETTLED' ? 'RE-PRINT' : ''}</b></span></div>
      <div class="foot"><span>Printed On</span><span>${fmtDmy(now)} ${fmtTime(now)}</span></div>
      <hr class="sep2"/>
      <div class="thanks">*** Thank You &amp; Visit Again ***</div>

    </div>

    </body>
    </html>
  `;
}

// Convenience: fetch a bill's full detail and open it in a print window. The
// browser's print dialog opens straight away (Print or Save as PDF); the bill
// itself carries no on-screen button, so nothing extra lands on paper or PDF.
// Every KOT & POS View / Print Bill uses this one template (buildBillPrintHtml).
export function openBillPrintWindow(data) {
  const w = window.open('', '_blank', 'width=420,height=760');
  if (!w) throw new Error('Enable popups to view the bill');
  w.document.write(buildBillPrintHtml(data));
  w.document.close();
  w.focus();
  setTimeout(() => { try { w.print(); } catch { /* window closed */ } }, 250);
  return w;
}

export async function viewKotBillById(billId, headers) {
  const res = await fetch(`/api/v1/kot/bills/${billId}`, { headers });
  const data = await res.json();
  if (!res.ok) throw new Error(data?.error || 'Failed to load bill');
  openBillPrintWindow(data);
  return data;
}
